import { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Box, Theme, Typography, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, IconButton, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import moment from 'moment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import clsx from 'clsx';

import Loader from '../../components/Loader';
import { VENDOR_FILE_UPLOAD_HISTORY_TABLE_LABELS } from '../../constants/tableLabels';
import { VendorFileUploadHistory, ErrorData } from '../../types';
import useSnackBar from '../../hooks/useSnackBar';
import usePagination from '../../hooks/usePagination';
import { getVendorFileUploadHistory, getVendorErrorDocument } from '../../api/vendorApi/vendorAccountsApi';
import TablePaginationLocalContainer from '../../components/Table/TablePaginationLocalContainer';

const useStyles = makeStyles()((theme: Theme) => ({
  fileUploadHistoryLabel: {
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '120%',
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  tableHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
    marginTop: '24px',

    '@media (max-width: 800px)': {
      flexDirection: 'column',
      maxHeight: 'auto',
      gap: '12px',
    },
  },
  tableWrapper: {
    background: theme.palette.primary.light,
    borderRadius: '8px',
    fontFamily: 'Inter',
    overflow: 'hidden',
  },
  tableContainer: {
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Inter',
    paddingBottom: '10px',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  tableHeaderCell: {
    background: theme.palette.primary.light,
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: '120px',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      minWidth: '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
}));

export interface VendorBulkTableRef {
  refresh: () => void;
}

const VendorBulkTable = forwardRef<VendorBulkTableRef>((props, ref) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { setAlert } = useSnackBar();
  const { pageNumber, rowsPerPage, setTotalCount, totalCount, totalPages, onChangePageNumber, onChangeRowsPerPage } = usePagination();
  const [uploadHistory, setUploadHistory] = useState<VendorFileUploadHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchUploadHistory = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getVendorFileUploadHistory({
        page: pageNumber,
        size: rowsPerPage,
        count: pageNumber === 1,
      });
      if (response.data.resultSet) {
        pageNumber === 1 && setTotalCount?.(response.data.totalCount || 0);
        setUploadHistory(response.data.resultSet);
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'Failed to fetch upload history',
        type: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  }, [pageNumber, rowsPerPage, setTotalCount]);


  // const fetchUploadHistoryStatusOnly = useCallback(async () => {
  //   try {
  //     const response = await getVendorFileUploadHistory({
  //       page: pageNumber,
  //       size: rowsPerPage,
  //       count: false,
  //     });
  //     const newData = response.data.resultSet || [];

  //     setUploadHistory((prevHistory) => {
  //       return prevHistory.map((prevItem) => {
  //         const newItem = newData.find((item) => item.id === prevItem.id);
  //         if (newItem && (newItem.status !== prevItem.status || newItem.completedAt !== prevItem.completedAt)) {
  //           return {
  //             ...prevItem,
  //             status: newItem.status,
  //             completedAt: newItem.completedAt,
  //             totalRows: newItem.totalRows,
  //             successCount: newItem.successCount,
  //             failureCount: newItem.failureCount,
  //             errorFileDownloadUrl: newItem.errorFileDownloadUrl,
  //           };
  //         }
  //         return prevItem;
  //       });
  //     });
  //   } catch {
  //     // Silent error - polling will retry
  //   }
  // }, [pageNumber, rowsPerPage]);

  useEffect(() => {
    fetchUploadHistory();
  }, [fetchUploadHistory]);

  useImperativeHandle(ref, () => ({
    refresh: fetchUploadHistory,
  }), [fetchUploadHistory]);

  // Auto-refresh polling for active uploads (UPLOADED or PROCESSING status)
  // useEffect(() => {
  //   const hasActiveUploads = uploadHistory.some(
  //     (item) => item.status === 'UPLOADED' || item.status === 'PROCESSING'
  //   );

  //   if (!hasActiveUploads) return;

  //   const pollInterval = setInterval(() => {
  //     fetchUploadHistoryStatusOnly();
  //   }, 5000); // Poll every 5 seconds

  //   return () => clearInterval(pollInterval);
  // }, [uploadHistory, fetchUploadHistoryStatusOnly]);

  const handleDownloadErrorFile = async (errorFilePath: string) => {
    try {
      const encodedDocumentKey = encodeURIComponent(errorFilePath);
      const response = await getVendorErrorDocument(encodedDocumentKey);

      if (response.data?.downloadUrl) {
        const fileUrl = response.data.downloadUrl;
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', '');
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setAlert((prev) => ({
          ...prev,
          message: 'No error file found to download.',
          type: 'info',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'Failed to download error file',
        type: 'error',
      }));
    }
  };

  const formattedTableData = uploadHistory.map((item) => ({
    id: item.id,
    fileName: item.originalFileName,
    status: item.status,
    totalRows: item.totalRows,
    successCount: item.successCount,
    failureCount: item.failureCount,
    startedAt: moment(item.startedAt).format('hh:mm A, M/D/YYYY'),
    completedAt: item.completedAt ? moment(item.completedAt).format('hh:mm A, M/D/YYYY') : '-',
    errorFile: item.errorFileDownloadUrl,
  }));

  const renderTableRows = () => {
    if (formattedTableData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ padding: '40px 0', textAlign: 'center !important' }}>
            <Typography variant="body1" color="textSecondary">
              No records found
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return formattedTableData.map((tableItem) => (
      <TableRow key={tableItem.id} sx={{ width: '100%', '&:hover': { cursor: 'default' } }}>
        {Object.entries(tableItem).map(([key, value], idx) => {
          if (key === 'id') return null;

          if (key === 'errorFile') {
            return (
              <TableCell key={idx}>
                {value ? (
                  <Tooltip title="Download error file">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadErrorFile(value as string)}
                      sx={{ color: theme.palette.common.white }}
                    >
                      <FileDownloadIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  '-'
                )}
              </TableCell>
            );
          }

          if (key === 'fileName') {
            return (
              <TableCell key={idx} sx={{
                maxWidth: '300px',
                whiteSpace: 'normal !important',
                overflow: 'visible',
                textOverflow: 'clip',
                wordBreak: 'break-word'
              }}>
                {value}
              </TableCell>
            );
          }

          return (
            <TableCell key={idx}>
              {value}
            </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  return (
    <Box>
      <Box className={classes.tableHeadline}>
        <Typography className={classes.fileUploadHistoryLabel}>Upload History</Typography>
      </Box>

      {isLoading && !uploadHistory.length ? (
        <Loader colorType="warning"></Loader>
      ) : (
        <Box className={classes.tableWrapper}>
          <Box className={classes.tableContainer}>
            <Table sx={{
              minWidth: '800px',
              tableLayout: 'auto',
            }}>
              <TableHead className={classes.tableHeaderCell}>
                <TableRow>
                  {VENDOR_FILE_UPLOAD_HISTORY_TABLE_LABELS.map((header) => (
                    <TableCell key={header.accessor || header.label}>
                      {header.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className={clsx(classes.tableBodyCell)}>
                {renderTableRows()}
              </TableBody>
            </Table>
          </Box>
          {onChangePageNumber && onChangeRowsPerPage && (
            <TablePaginationLocalContainer
              count={totalCount}
              page={pageNumber - 1}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, newPage) => onChangePageNumber(_, newPage + 1)}
              onRowsPerPageChange={onChangeRowsPerPage}
              totalPages={totalPages}
            />
          )}
        </Box>
      )}
    </Box>
  );
});

VendorBulkTable.displayName = 'VendorBulkTable';

export default VendorBulkTable;